import {
  ArrowLeftRight,
  Bell,
  CalendarRange,
  CalendarSearch,
  ClipboardList,
  Gauge,
  MapPin,
} from 'lucide-react';
import type { NavLeaf } from './navItems.js';

/**
 * Pages that are reachable but deliberately absent from the rendered nav, so
 * ⌘K can still jump to them. Notifications lives only in the top-bar bell and
 * the mobile "Alerts" tab (not the sidebar), so it would otherwise be the one
 * real page the palette couldn't reach. These are NOT rendered by the Sidebar
 * or MobileBottomNav — they're appended to `allDestinations()` only.
 */
export const EXTRA_DESTINATIONS: NavLeaf[] = [
  // THE OLD OVERVIEW. Since Overview and Operations became one page
  // (2026-10-15) `/overview` redirects to `/operations` for anyone who may see
  // the floor, and shows only the decisions card to a role that may not. Listed
  // so `isAdminPath` still reads it as the back office; named for what it now
  // holds, because the rail's "Overview" is the merged page.
  {
    to: '/overview',
    labelKey: 'nav.decisions',
    label: 'Needs a decision',
    icon: Gauge,
    roles: ['ADMIN', 'HR', 'MANAGER'],
  },
  { to: '/notifications', labelKey: 'nav.notifications', label: 'Notifications', icon: Bell },
  // THE MONTH VIEW OF THE LEAVE SCREEN. Same case as the people map below and
  // the calendar's own day view: one view of a destination already in the rail,
  // reached from that screen's two-tab strip, so a rail row for it would read
  // as a second page. It keeps a palette jump because "who is off in September"
  // is a question people search for by name.
  //
  // It is also what keeps `isAdminPath` right here — see the note on that
  // function. A role-gated destination the app knows about is an admin
  // destination whether or not it earned a rail row.
  {
    to: '/leave/calendar',
    labelKey: 'nav.leaveCalendar',
    label: 'Leave calendar',
    icon: CalendarRange,
    permission: 'leave.requests.view',
    feature: 'leave',
  },
  // THE INBOUND HALF OF THE ERP SCREEN. Same case as the leave calendar above:
  // one view of a destination already in the rail, reached from that screen's
  // own two-tab strip, so a rail row for it would read as a second page. Two
  // rows at one subject is what the Devices note further up ruled out.
  //
  // It is here rather than nowhere for the reason that note gives: this is what
  // keeps `isAdminPath` right. A role-gated destination the app knows about is
  // an admin destination whether or not it earned a rail row — without this,
  // `/erp/changes` would read as a personal page and the admin console would
  // drop its chrome on the tab a reader just switched to.
  //
  // It also earns a palette jump on its own: "what did the ERP change" is a
  // question people search for by name.
  {
    to: '/erp/changes',
    labelKey: 'nav.erpChanges',
    label: 'ERP changes',
    icon: ArrowLeftRight,
    permission: 'erp.view',
    feature: 'erp',
  },
  // THE CONNECTION HALF OF THE SAME SCREEN, and here for exactly the reason
  // above: without it `isAdminPath` reads `/erp/connection` as a personal page
  // and the admin console drops its chrome on the tab a reader just switched
  // to. No rail row, for the same reason again — one subject, one row.
  //
  // The palette jump earns itself: "where do I point Hadir at the ERP" is a
  // question somebody asks once, urgently, by name, and never finds by browsing
  // an approvals queue.
  {
    to: '/erp/connection',
    labelKey: 'nav.erpConnection',
    label: 'ERP connection',
    icon: ArrowLeftRight,
    permission: 'erp.manage',
    feature: 'erp',
  },
  // The people-pinned view of the control room. It gets no sidebar row — a
  // second row for one view of a page already in the rail reads as a second
  // page — but it is worth a palette jump, because "where is everyone standing"
  // is a question people search for by name rather than navigate to.
  //
  // Points at the LIVE url, with its view state. It used to point at
  // `/live/map`, which has been a redirect since the four boards merged, so the
  // palette's own entry bounced through it — invisible in the UI, and invisible
  // to the wiring test until that test learned to reject redirect targets.
  {
    to: '/operations?view=map&pins=people',
    labelKey: 'nav.peopleMap',
    label: 'People map',
    icon: MapPin,
    permission: 'siteops.view',
  },
  // The DAY half of the attendance calendar. It gets no sidebar row for exactly
  // the reason the people map does not: it is one view of a destination already
  // in the rail, and a second row would read as a second page — worse here,
  // because `pathMatches` would light BOTH rows on /attendance-calendar/day.
  //
  // It is still worth a palette jump. "Who was in on the 12th" is a question
  // people search for by name rather than navigate to, and the alternative is
  // opening the month and clicking a column header to get somewhere the app
  // already has an address for.
  //
  // No `date` in the URL on purpose: the page resolves a missing or unusable
  // date to today, which is what somebody typing "day" into the palette means.
  //
  // `CalendarSearch`, not `CalendarDays`: the palette lists this beside "My
  // leave", which owns that glyph, and the one-picture-one-destination rule is
  // about what a reader can tell apart, not about which surface renders it.
  {
    to: '/attendance-calendar/day',
    labelKey: 'nav.attendanceDay',
    label: 'Attendance by day',
    icon: CalendarSearch,
    permission: 'attendance.view',
  },
  // BULK IMPORT, off the rail and into the palette. It is a child route of
  // /employees and is reached from that page's header action; listing it in
  // the sidebar drew it as the Employees row's SIBLING, which is a hierarchy
  // the routes contradict. `hideInConstruction` travels with it unchanged.
  {
    to: '/employees/import',
    labelKey: 'nav.bulkImport',
    label: 'Bulk import',
    icon: ClipboardList,
    roles: ['ADMIN', 'HR'],
    hideInConstruction: true,
  },
  // The attendance workspace is NOT here any more — it is a TOP_LEVEL rail row
  // (see `REPORTS_WORKSPACE`). Listing it in both would put the same
  // destination in the palette twice.
];
